import React, { useRef, useState } from "react";
import { useSupplierRetailer } from "../../../wrappers/SupplierRetailerContext";
import { Box, Button, Text, Table, Thead, Tbody, Tr, Th, Td, Badge, Checkbox, Input, HStack, useToast } from "@chakra-ui/react";

/**
 * Multi-file PnP Strike Rate upload with validate-then-commit (POST /data/bulk-strikerate).
 * Same shape as BulkSalesUpload: validate every file first (no writes), review the
 * per-file verdicts, then import everything that passed. Rows are matched to
 * suppliers by PnP vendor number, so one file carrying several suppliers (Gateway)
 * imports each row under its own supplier — the supplier picked in the TopBar does
 * not decide attribution. Re-uploading a week is safe: strike rate has no delete
 * step, and reasons set on the Strike Rate page are kept.
 */

const VERDICT_COLORS = { PASS: "green", WARN: "orange", FAIL: "red" };
const LEVEL_COLORS = { error: "red", warning: "orange", info: "blue" };

const exampleText = (ex) =>
  Object.entries(ex)
    .filter(([, v]) => v != null && v !== "")
    .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join("/") : v}`)
    .join(", ");

const weeksText = (s) => {
  if (!s || !s.weekFrom) return "-";
  return s.weekFrom === s.weekTo ? s.weekFrom : `${s.weekFrom} → ${s.weekTo} (${s.weeks})`;
};

const suppliersText = (s) => {
  if (!s || !s.suppliers || !s.suppliers.length) return "-";
  return s.suppliers.map((x) => `${x.supplier} ${x.rows}`).join(" · ") + (s.heldRows ? ` · held ${s.heldRows}` : "");
};

const BulkStrikeRateUpload = ({ supplier, onImported }) => {
  // The organisation selected at the top — the server refuses another organisation's suppliers (2026-10-06).
  const { selectedOrg } = useSupplierRetailer();
  const [rows, setRows] = useState([]); // {id, file, name, sizeKB, state, verdict, summary, issues, fileKey, imported, message}
  const [busy, setBusy] = useState(false);
  const [acceptWarnings, setAcceptWarnings] = useState(false);
  const fileInput = useRef();
  const toast = useToast();

  const pickFiles = (e) => {
    const picked = Array.from(e.target.files || []).map((f, i) => ({
      id: `${Date.now()}-${i}-${f.name}`,
      file: f,
      name: f.name,
      sizeKB: Math.round(f.size / 1024),
      state: "pending", // pending -> validating -> validated -> importing -> imported | failed
      verdict: null,
      summary: null,
      issues: [],
      fileKey: null,
      imported: null,
      message: "",
    }));
    setRows(picked);
  };

  const patchRow = (id, patch) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const call = async (formData) => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/data/bulk-strikerate`, { method: "POST", body: formData });
    return { httpStatus: res.status, body: await res.json() };
  };

  const base = () => {
    const fd = new FormData();
    fd.append("retailer", "pnp");
    fd.append("supplier", supplier);
    return fd;
  };

  const validateAll = async () => {
    setBusy(true);
    for (const r of rows) {
      if (r.state === "imported") continue;
      patchRow(r.id, { state: "validating", message: "" });
      try {
        const fd = base();
        fd.append("xlsxFile", r.file);
        fd.append("mode", "validate");
        fd.append("org_code", selectedOrg || "");
        const { body } = await call(fd);
        if (body.verdict) {
          patchRow(r.id, { state: body.verdict === "FAIL" && !body.fileKey ? "failed" : "validated", verdict: body.verdict, summary: body.summary || null, issues: body.issues || [], fileKey: body.fileKey || null, message: body.message || "" });
        } else {
          patchRow(r.id, { state: "failed", verdict: "FAIL", message: body.message || "Validation failed" });
        }
      } catch (err) {
        patchRow(r.id, { state: "failed", verdict: "FAIL", message: String(err.message || err) });
      }
    }
    setBusy(false);
  };

  const importAll = async () => {
    setBusy(true);
    const runId = "bulksr-" + Date.now() + "-" + Math.random().toString(36).slice(2, 8);
    let ok = 0;
    let fail = 0;
    for (const r of rows) {
      const eligible = r.state === "validated" && (r.verdict === "PASS" || (r.verdict === "WARN" && acceptWarnings));
      if (!eligible) continue;
      patchRow(r.id, { state: "importing" });
      try {
        const fd = base();
        fd.append("mode", "commit");
        fd.append("org_code", selectedOrg || "");
        fd.append("fileKey", r.fileKey);
        fd.append("acceptWarnings", acceptWarnings ? "true" : "false");
        fd.append("runId", runId);
        const { body } = await call(fd);
        if (body.status === 1 && body.import) {
          ok++;
          patchRow(r.id, { state: "imported", imported: body.import, message: body.message || "" });
        } else {
          fail++;
          patchRow(r.id, { state: "failed", verdict: body.verdict || r.verdict, issues: body.issues || r.issues, message: body.message || "Import failed" });
        }
      } catch (err) {
        fail++;
        patchRow(r.id, { state: "failed", message: String(err.message || err) });
      }
    }
    setBusy(false);
    toast({ title: "Bulk strike rate finished", description: `${ok} file(s) imported, ${fail} failed`, status: fail ? "warning" : "success", duration: 6000, isClosable: true });
    // New Data finish-run — fire-and-forget, never blocks the UI
    const fd = base();
    fd.append("mode", "finish-run");
    fd.append("runId", runId);
    call(fd).catch(() => {});
    if (typeof onImported === "function") { try { onImported(); } catch (e) {} }
  };

  const validated = rows.filter((r) => r.state === "validated");
  const importable = validated.filter((r) => r.verdict === "PASS" || (r.verdict === "WARN" && acceptWarnings));
  const totalInserted = rows.reduce((a, r) => a + (r.imported ? r.imported.inserted : 0), 0);
  const totalHeld = rows.reduce((a, r) => a + (r.imported ? r.imported.held || 0 : 0), 0);

  return (
    <Box>
      <Text fontSize="sm" color="gray.500" mb={2}>
        Select one or many PnP Strike Rate files. Rows are matched to suppliers by <b>PnP vendor number</b>, so a file with several
        suppliers imports each row under its own supplier. Step 1 validates every file (nothing is written). Step 2 imports the
        files that passed. Re-uploading a week is safe — reasons set on the Strike Rate page are kept.
      </Text>
      <HStack mb={3} spacing={3}>
        <Input type="file" accept=".xlsx" multiple ref={fileInput} onChange={pickFiles} maxW="360px" p={1} />
        <Button colorScheme="blue" onClick={validateAll} isDisabled={!rows.length || busy} isLoading={busy && rows.some((r) => r.state === "validating")}>
          1. Validate all ({rows.length})
        </Button>
        <Button colorScheme="green" onClick={importAll} isDisabled={!importable.length || busy} isLoading={busy && rows.some((r) => r.state === "importing")}>
          2. Import {importable.length} passed
        </Button>
        <Checkbox isChecked={acceptWarnings} onChange={(e) => setAcceptWarnings(e.target.checked)}>
          include files with warnings
        </Checkbox>
      </HStack>

      {rows.length > 0 && (
        <Table size="sm" variant="striped">
          <Thead>
            <Tr>
              <Th>File</Th>
              <Th>Size</Th>
              <Th>Status</Th>
              <Th>Rows</Th>
              <Th>Week ending</Th>
              <Th>Suppliers (rows)</Th>
              <Th>Ordered / Received</Th>
              <Th>Imported</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <Tr>
                  <Td fontSize="xs" maxW="240px" isTruncated title={r.name}>{r.name}</Td>
                  <Td fontSize="xs">{r.sizeKB} KB</Td>
                  <Td>
                    {r.verdict ? (
                      <Badge colorScheme={VERDICT_COLORS[r.verdict] || "gray"}>{r.state === "imported" ? "IMPORTED" : r.state === "importing" ? "IMPORTING…" : r.state === "failed" && r.verdict !== "FAIL" ? "ERROR" : r.verdict}</Badge>
                    ) : (
                      <Badge>{r.state}</Badge>
                    )}
                  </Td>
                  <Td fontSize="xs">{r.summary ? `${r.summary.importableRows}/${r.summary.parsedRows}` : "-"}</Td>
                  <Td fontSize="xs">{weeksText(r.summary)}</Td>
                  <Td fontSize="xs">{suppliersText(r.summary)}</Td>
                  <Td fontSize="xs">{r.summary && r.summary.orderQty != null ? `${r.summary.orderQty} / ${r.summary.receiptQty}${r.summary.strikeRate != null ? ` (${r.summary.strikeRate}%)` : ""}` : "-"}</Td>
                  <Td fontSize="xs">{r.imported ? `${r.imported.inserted} rows${r.imported.held ? `, ${r.imported.held} held` : ""} (${(r.imported.ms / 1000).toFixed(1)}s)` : "-"}</Td>
                </Tr>
                {/* Every reason, always visible — a FAIL the user cannot read the cause of looks like a broken feature. */}
                {((r.issues && r.issues.length) || r.message) && (
                  <Tr>
                    <Td colSpan={8} bg="gray.50" py={2}>
                      {r.message && (
                        <Text fontSize="xs" fontWeight="bold" color={r.verdict === "FAIL" || r.state === "failed" ? "red.600" : "gray.700"} mb={r.issues && r.issues.length ? 2 : 0}>
                          {r.message}
                        </Text>
                      )}
                      {(r.issues || []).map((i, idx) => (
                        <Box key={idx} mb={idx < r.issues.length - 1 ? 2 : 0}>
                          <HStack spacing={2} align="baseline">
                            <Badge colorScheme={LEVEL_COLORS[i.level] || "gray"}>{i.level}</Badge>
                            <Text fontSize="xs" fontWeight="semibold">
                              {i.message} ({i.count})
                            </Text>
                          </HStack>
                          {i.examples && i.examples.length > 0 && (
                            <Text fontSize="xs" color="gray.600" ml={1} mt={0.5}>
                              e.g. {i.examples.map(exampleText).join("  •  ")}
                              {i.count > i.examples.length ? `  … and ${i.count - i.examples.length} more` : ""}
                            </Text>
                          )}
                        </Box>
                      ))}
                    </Td>
                  </Tr>
                )}
              </React.Fragment>
            ))}
          </Tbody>
        </Table>
      )}

      {totalInserted > 0 && (
        <Text mt={3} fontWeight="bold" fontSize="sm">
          Total rows imported this session: {totalInserted.toLocaleString()}
          {totalHeld > 0 ? ` — ${totalHeld} held for review (Imports → New Data)` : ""}
        </Text>
      )}
    </Box>
  );
};

export default BulkStrikeRateUpload;
